"use client";

import { c as _c } from "react/compiler-runtime";
import { Toggle } from "@base-ui/react/toggle";
import { ToggleGroup } from "@base-ui/react/toggle-group";
import type { CentralIconBaseProps } from "central-icons/CentralIconBase";
import { cn } from "cn";
import { m } from "motion/react";
import { useId } from "react";
import { springTransitions } from "@/lib/animation-presets";
export interface SegmentedControlOption<T extends string> {
  icon?: React.ComponentType<CentralIconBaseProps>;
  label: string;
  value: T;
}
interface SegmentedControlProps<T extends string> {
  "aria-label"?: string;
  className?: string;
  disabled?: boolean;
  onValueChange: (value: T) => void;
  options: readonly SegmentedControlOption<T>[];
  /** `null` presses nothing, e.g. before the value is known. */
  value: T | null;
  /** `text` shows labels with a sliding indicator; `icon` shows icons only. */
  variant?: "text" | "icon";
}
export function SegmentedControl(t0) {
  const $ = _c(23);
  const {
    value,
    onValueChange,
    options,
    disabled,
    className,
    variant: t1,
    "aria-label": ariaLabel
  } = t0;
  const variant = t1 === undefined ? "text" : t1;
  const indicatorId = useId();
  const isText = variant === "text";
  const t2 = isText ? "grid h-10 w-full auto-cols-fr grid-flow-col gap-1 rounded-lg p-1" : "flex items-center gap-0.5 rounded-full p-0.5";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("bg-surface shadow-raised", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onValueChange) {
    t4 = next => {
      const [pressed] = next;
      if (pressed !== undefined) {
        onValueChange(pressed as T);
      }
    };
    $[3] = onValueChange;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== value) {
    t5 = value === null ? [] : [value];
    $[5] = value;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== indicatorId || $[8] !== isText || $[9] !== options || $[10] !== value) {
    let t7;
    if ($[12] !== indicatorId || $[13] !== isText || $[14] !== value) {
      t7 = option => <Toggle aria-label={isText ? undefined : option.label} className={cn("group transition-colors ease-out", isText ? "relative flex h-full w-full items-center justify-center gap-1.5 font-medium text-base text-fg-muted duration-200 data-pressed:text-fg" : "inline-flex size-7 items-center justify-center rounded-full text-fg-muted duration-150 hover:text-fg data-pressed:bg-gray-300 data-pressed:text-fg")} key={option.value} value={option.value}>{isText ? <>{value === option.value && <m.div className="absolute inset-0 rounded bg-gray-300" initial={false} layoutId={indicatorId} transition={springTransitions.normal} />}<span className="z-(--z-above) select-none text-sm transition-colors duration-200 ease-out group-hover:text-fg">{option.label}</span></> : option.icon && <option.icon aria-hidden="true" className="size-4" mode="raw" />}</Toggle>;
      $[12] = indicatorId;
      $[13] = isText;
      $[14] = value;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    t6 = options.map(t7);
    $[7] = indicatorId;
    $[8] = isText;
    $[9] = options;
    $[10] = value;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[16] !== ariaLabel || $[17] !== disabled || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== t6) {
    t7 = <ToggleGroup aria-label={ariaLabel} className={t3} disabled={disabled} onValueChange={t4} value={t5}>{t6}</ToggleGroup>;
    $[16] = ariaLabel;
    $[17] = disabled;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}